// Updated EmailVerification.js component
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { authService } from '../../services/authService';
import { usePageTitle } from '../../hooks/usePageTitle';
import './EmailVerification.css';

export const EmailVerification = () => {
    usePageTitle('Email Verification');
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [message, setMessage] = useState('');
    const isMountedRef = useRef(true);

    useEffect(() => {
        isMountedRef.current = true;

        const verifyEmail = async () => {
            const token = searchParams.get('token');

            if (!token) {
                if (isMountedRef.current) {
                    setStatus('error');
                    setMessage('Invalid verification link. Token is missing.');
                }
                return;
            }

            try {
                console.log('Starting email verification with token:', token);
                const response = await authService.verifyEmail(token);

                console.log('Verification response:', response);

                if (isMountedRef.current) {
                    setStatus('success');
                    setMessage(response.message || 'Email verified successfully!');

                    // Auto redirect to login after 5 seconds
                    setTimeout(() => {
                        if (isMountedRef.current) {
                            navigate('/login?verified=true');
                        }
                    }, 5000);
                }
            } catch (error) {
                console.error('Verification error:', error);

                if (isMountedRef.current) {
                    // Check if the error message indicates success (for edge cases)
                    const errorMsg = error.message || '';

                    if (errorMsg.includes('already verified') ||
                        errorMsg.includes('already been verified') ||
                        errorMsg.includes('account is now active')) {
                        setStatus('success');
                        setMessage('This email has already been verified. You can now log in to your account.');
                        setTimeout(() => {
                            if (isMountedRef.current) {
                                navigate('/login?verified=true');
                            }
                        }, 3000);
                    } else {
                        setStatus('error');
                        // More specific error handling
                        if (errorMsg.includes('expired') || errorMsg.includes('Invalid') || errorMsg.includes('invalid')) {
                            setMessage('This verification link has expired or is invalid. Please request a new verification email.');
                        } else {
                            setMessage(errorMsg || 'Verification failed. Please try again or contact support.');
                        }
                    }
                }
            }
        };

        // Add a small delay to show the loading state briefly
        const timeoutId = setTimeout(() => {
            verifyEmail();
        }, 500);

        return () => {
            isMountedRef.current = false;
            clearTimeout(timeoutId);
        };
    }, [searchParams, navigate]);

    return (
        <div className="verification-page">
            <div className="verification-container">
                <div className="verification-content">
                    {status === 'verifying' && (
                        <>
                            <div className="loading-icon">
                                <div className="spinner"></div>
                            </div>
                            <h2>Verifying Your Email</h2>
                            <p>Please wait while we verify your email address...</p>
                            <div className="verification-progress">
                                <div className="progress-bar">
                                    <div className="progress-fill"></div>
                                </div>
                                <small>This may take a few seconds...</small>
                            </div>
                        </>
                    )}

                    {status === 'success' && (
                        <>
                            <div className="success-icon">
                                <i className="fas fa-check-circle"></i>
                            </div>
                            <h2>Email Verified Successfully!</h2>
                            <p>{message}</p>
                            <div className="next-steps">
                                <h3>What's next?</h3>
                                <ul>
                                    <li>Your organization is now pending approval</li>
                                    <li>Our team will review your registration</li>
                                    <li>You'll receive an email once approved</li>
                                    <li>Then you can start using eHAAZRI!</li>
                                </ul>
                            </div>
                            <div className="redirect-notice">
                                <p>You'll be redirected to login in a few seconds...</p>
                            </div>
                            <div className="action-buttons">
                                <Link to="/login" className="btn btn-primary">
                                    <i className="fas fa-sign-in-alt"></i>
                                    Go to Login Now
                                </Link>
                            </div>
                        </>
                    )}

                    {status === 'error' && (
                        <>
                            <div className="error-icon">
                                <i className="fas fa-times-circle"></i>
                            </div>
                            <h2>Verification Failed</h2>
                            <p>{message}</p>

                            <div className="error-actions">
                                {(message.includes('expired') || message.includes('invalid')) ? (
                                    <>
                                        <ResendVerificationForm />
                                        <div className="action-divider">or</div>
                                        <Link to="/register" className="btn btn-secondary">
                                            <i className="fas fa-user-plus"></i>
                                            Register Again
                                        </Link>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => window.location.reload()}
                                            className="btn btn-secondary"
                                        >
                                            <i className="fas fa-redo"></i>
                                            Try Again
                                        </button>
                                        <Link to="/login" className="btn btn-primary">
                                            <i className="fas fa-sign-in-alt"></i>
                                            Go to Login
                                        </Link>
                                    </>
                                )}
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// Resend verification form component (unchanged)
const ResendVerificationForm = () => {
    const [email, setEmail] = useState('');
    const [status, setStatus] = useState('form'); // 'form', 'loading', 'success', 'error'
    const [message, setMessage] = useState('');

    const handleResend = async (e) => {
        e.preventDefault();

        if (!email.trim() || !/\S+@\S+\.\S+/.test(email)) {
            setStatus('error');
            setMessage('Please enter a valid email address');
            return;
        }

        setStatus('loading');

        try {
            await authService.resendVerification(email);
            setStatus('success');
            setMessage('Verification email sent successfully! Please check your inbox.');
        } catch (error) {
            setStatus('error');
            setMessage(error.message || 'Failed to send verification email');
        }
    };

    if (status === 'success') {
        return (
            <div className="resend-success">
                <div className="success-message">
                    <i className="fas fa-paper-plane"></i>
                    <p>{message}</p>
                </div>
            </div>
        );
    }

    return (
        <div className="resend-form">
            <h4>Resend Verification Email</h4>
            <form onSubmit={handleResend}>
                <div className="form-group">
                    <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Enter your email address"
                        required
                    />
                </div>
                {status === 'error' && (
                    <div className="error-message">
                        <i className="fas fa-exclamation-triangle"></i>
                        {message}
                    </div>
                )}
                <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    disabled={status === 'loading'}
                >
                    {status === 'loading' ? (
                        <>
                            <div className="spinner-sm"></div>
                            Sending...
                        </>
                    ) : (
                        <>
                            <i className="fas fa-paper-plane"></i>
                            Resend Email
                        </>
                    )}
                </button>
            </form>
        </div>
    );
};